// SAM-1095 — an article or an ad is saved as Draft or Published only. Scheduled and Expired are worked out
// from the two dates, so there is no new value in the database and old items fix themselves.
import { TIME_ZONE } from '../i18n/format';

export type CmsStatus = 'draft' | 'published';
export type CmsState = 'draft' | 'scheduled' | 'published' | 'expired';

export interface CmsDated {
  status?: string | null;
  publish_date?: string | null;
  expiration_date?: string | null;
}

const DAY_MS = 86_400_000;

/** Calendar day (YYYY-MM-DD) of an instant in the business clock; a bare date stays as it is. */
export const dayOf = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  // 'YYYY-MM-DD' and zone-less timestamps ('...T00:00:00', how the CRUD returns dates) are already a calendar day.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T[\d:.]*)?$/.test(value)) return value.slice(0, 10);
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
};

export const todayInBusinessClock = (now: Date = new Date()): string => dayOf(now) as string;

const dayNumber = (day: string): number => {
  const [y, m, d] = day.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export const daysBetween = (from: string, to: string): number => dayNumber(to) - dayNumber(from);

/**
 * Draft stays Draft. A Published item is Scheduled until its Publishes day, Published through its Expires day
 * (that day included) and Expired the day after. A missing date means "already" and "never".
 */
export const cmsState = (item: CmsDated, today: string = todayInBusinessClock()): CmsState => {
  if (item.status !== 'published') return 'draft';
  const start = dayOf(item.publish_date);
  const end = dayOf(item.expiration_date);
  if (start && daysBetween(today, start) > 0) return 'scheduled';
  if (end && daysBetween(end, today) > 0) return 'expired';
  return 'published';
};

export interface RelativeWords {
  today: string;
  tomorrow: string;
  yesterday: string;
  inDays: (n: number) => string;
  daysAgo: (n: number) => string;
}

export const EN_RELATIVE: RelativeWords = {
  today: 'today',
  tomorrow: 'tomorrow',
  yesterday: 'yesterday',
  inDays: (n) => `in ${n} days`,
  daysAgo: (n) => `${n} days ago`,
};

/** "in 6 days" / "tomorrow" / "16 days ago" for a calendar day, relative to `today`. */
export const relativeDay = (day: string | null | undefined, today: string = todayInBusinessClock(), w: RelativeWords = EN_RELATIVE): string => {
  const d = dayOf(day);
  if (!d) return '';
  const n = daysBetween(today, d);
  if (n === 0) return w.today;
  if (n === 1) return w.tomorrow;
  if (n === -1) return w.yesterday;
  return n > 0 ? w.inDays(n) : w.daysAgo(-n);
};

/** The small grey line under the Expires date in the Articles list: only while it still matters. */
export const expiresHint = (item: CmsDated, today: string = todayInBusinessClock()): string => {
  const state = cmsState(item, today);
  if (state === 'draft' || !item.expiration_date) return '';
  return relativeDay(item.expiration_date, today);
};

/** The grey line in the Ads list: "ends tomorrow", "starts in 20 days", "expired 16 days ago". */
export const adHint = (item: CmsDated, today: string = todayInBusinessClock()): string => {
  const state = cmsState(item, today);
  const start = dayOf(item.publish_date);
  const end = dayOf(item.expiration_date);
  if (state === 'scheduled' && start) return `starts ${relativeDay(start, today)}`;
  if (state === 'expired' && end) return `expired ${relativeDay(end, today)}`;
  if (state === 'published' && end) {
    const n = daysBetween(today, end);
    if (n <= 1) return `ends ${relativeDay(end, today)}`;
  }
  return '';
};

export const STATE_META: Record<CmsState, { label: string; bg: string; fg: string }> = {
  draft: { label: 'Draft', bg: '#FEF3C7', fg: '#92400E' },
  scheduled: { label: 'Scheduled', bg: '#DBEAFE', fg: '#1E40AF' },
  published: { label: 'Published', bg: '#D1FAE5', fg: '#065F46' },
  expired: { label: 'Expired', bg: '#F3F4F6', fg: '#4B5563' },
};

/** Move one id to a new index; returns the same array when nothing moves. */
export const moveId = (ids: number[], from: number, to: number): number[] => {
  if (from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) return ids;
  const next = [...ids];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
};
